import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Drawer, Form, Select, Row, Col, Spin, Popconfirm, message, Tag, List } from 'antd';
import reqwest from 'reqwest';
import moment from 'moment';
import { getToken } from '../../Utils/common';
import config from '../../config';

const { Option } = Select;

function HospitalizationProfessionals({ hospitalizationId, hospitalizationStatus, isReadOnly }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profForm] = Form.useForm();
  const [profOptions, setProfOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;
  const canOperate = !!hospitalizationId && isHospitalizationActive;

  const fetchList = () => {
    if (!hospitalizationId) return;
    setLoading(true);
    reqwest({
      url: `${config.api_url}/hospitalizationprofesional/?hospitalization=${hospitalizationId}`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(response => {
      const results = Array.isArray(response) ? response : (response.results || []);
      setData(results);
      setLoading(false);
    }).catch(error => {
      setLoading(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        message.error(`Error al cargar profesionales: ${error.status}`);
      }
    });
  };

  const fetchHistory = () => {
    if (!hospitalizationId) { setHistory([]); return; }
    setHistoryLoading(true);
    reqwest({
      url: `${config.api_url}/hospitalizationprofesionalhistory/?hospitalization=${hospitalizationId}`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(resp => {
      const results = Array.isArray(resp) ? resp : (resp.results || []);
      setHistory(results);
    }).catch(() => {
      message.error('Error al cargar historial de profesionales');
    }).always(() => setHistoryLoading(false));
  };

  useEffect(() => {
    fetchList();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalizationId]);

  const onOpenDrawer = () => {
    profForm.resetFields();
    setDrawerVisible(true);
  };

  const onCloseDrawer = () => setDrawerVisible(false);

  const searchProfessionals = (searchText) => {
    setSearching(true);
    reqwest({
      url: `${config.api_url}/profesionals/?search=${encodeURIComponent(searchText)}&limit=10`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(response => {
      setSearching(false);
      const results = response.results || [];
      const assigned = new Set((data || []).map(d => d.profesional));
      const options = results
        .filter(p => !assigned.has(p.id))
        .map(p => ({
          value: p.id,
          label: `${p.name || ''} ${p.last_name || ''}`.trim()
        }));
      setProfOptions(options);
    }).catch(error => {
      setSearching(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        message.error(`Error al buscar profesionales: ${error.status}`);
      }
    });
  };

  const onSubmit = async (values) => {
    if (!hospitalizationId) return;
    if (!values.profesional) {
      message.error('Debe seleccionar un profesional');
      return;
    }
    setSaving(true);
    try {
      await reqwest({
        url: `${config.api_url}/hospitalizationprofesional/`,
        method: 'post',
        type: 'json',
        data: {
          hospitalization: hospitalizationId,
          profesional: values.profesional,
        },
        headers: { 'Authorization': `Token ${getToken()}` },
      });
      setSaving(false);
      setDrawerVisible(false);
      profForm.resetFields();
      message.success('Profesional agregado a la internación');
      fetchList();
      fetchHistory();
    } catch (error) {
      setSaving(false);
      if (error && error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        const status = (error && error.status) || '';
        message.error(`Error al agregar profesional: ${status}`);
      }
    }
  };

  const onDelete = (record) => {
    setLoading(true);
    reqwest({
      url: `${config.api_url}/hospitalizationprofesional/${record.id}/`,
      method: 'delete',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then(() => {
      message.success('Profesional eliminado de la internación');
      fetchList();
      fetchHistory();
    }).catch(error => {
      setLoading(false);
      if (error.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        message.error(`Error al eliminar: ${error.status}`);
      }
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '8%' },
    { title: 'Nombre', dataIndex: 'profesional_name', key: 'profesional_name' },
    { title: 'Apellido', dataIndex: 'profesional_last_name', key: 'profesional_last_name' },
    { title: 'Especialidad', dataIndex: 'profesional_speciality_display', key: 'profesional_speciality_display' },
    {
      title: 'Acciones', key: 'actions', width: '16%',
      render: (text, record) => (
        <Popconfirm title="¿Eliminar profesional de la internación?" onConfirm={() => onDelete(record)} okText="Sí" cancelText="No" disabled={!isHospitalizationActive}>
          <Button danger size="small" disabled={!isHospitalizationActive}>Eliminar</Button>
        </Popconfirm>
      )
    }
  ];

  const historyColumns = [
    { title: 'Fecha/Hora', dataIndex: 'changed_at', key: 'changed_at' },
    { title: 'Acción', dataIndex: 'action', key: 'action', render: (v) => v === 'DEL' ? <Tag color="red">Eliminado</Tag> : <Tag color="green">Agregado</Tag> },
    { title: 'Nombre', dataIndex: 'profesional_name', key: 'profesional_name' },
    { title: 'Apellido', dataIndex: 'profesional_last_name', key: 'profesional_last_name' },
    { title: 'Registrado por', dataIndex: 'changed_by_username', key: 'changed_by_username' },
  ];

  return (
    <Card title="Profesionales" extra={
      <Button type="primary" onClick={onOpenDrawer} disabled={!canOperate}>Agregar</Button>
    }>
      {!canOperate && (
        <div style={{ marginBottom: 12, color: '#999' }}>Seleccione una internación para gestionar sus profesionales.</div>
      )}
      <Spin spinning={loading}>
        <Table rowKey={r => r.id} columns={columns} dataSource={data} pagination={false} />
      </Spin>

      <div style={{ marginTop: 24 }}>
        <Card title="Historial de profesionales asignados">
          <Spin spinning={historyLoading}>
            {!canOperate && <div style={{ marginBottom: 12, color: '#999' }}>Seleccione una internación para ver el historial.</div>}
            {canOperate && (
              history.length === 0 ? (
                <div style={{ color: '#999' }}>Sin movimientos.</div>
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={history}
                  renderItem={(item) => {
                    let color = 'default';
                    let label = item.action;
                    switch (item.action) {
                      case 'ADD':
                        color = 'green';
                        label = 'Agregado';
                        break;
                      case 'DEL':
                        color = 'red';
                        label = 'Eliminado';
                        break;
                      default:
                        color = 'default';
                        label = item.action;
                    }
                    const fullName = `${item.profesional_name || ''} ${item.profesional_last_name || ''}`.trim() || '-';
                    return (
                      <List.Item key={item.id}>
                        <List.Item.Meta
                          title={
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ color: '#555' }}>{moment(item.changed_at).format('DD/MM/YYYY HH:mm')}</span>
                              <Tag color={color}>{label}</Tag>
                              <span style={{ fontWeight: 500 }}>{fullName}</span>
                            </div>
                          }
                          description={<div style={{ color: '#888' }}>Por: {item.changed_by_username || '-'}</div>}
                        />
                      </List.Item>
                    );
                  }}
                />
              )
            )}
          </Spin>
        </Card>
      </div>

      <Drawer
        title="Agregar profesional"
        width={520}
        visible={drawerVisible}
        onClose={onCloseDrawer}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={onCloseDrawer} style={{ marginRight: 8 }}>Cancelar</Button>
            <Button type="primary" loading={saving} onClick={() => profForm.submit()}>Agregar</Button>
          </div>
        }
      >
        <Form form={profForm} layout="vertical" onFinish={onSubmit}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="profesional" label="Seleccionar profesional" rules={[{ required: true, message: 'Seleccione un profesional' }]}>
                <Select
                  showSearch
                  placeholder="Buscar por nombre o apellido"
                  filterOption={false}
                  onSearch={searchProfessionals}
                  notFoundContent={searching ? <Spin size="small" /> : 'Sin resultados'}
                  allowClear
                >
                  {profOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </Card>
  );
}

export default HospitalizationProfessionals;
